import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ClipboardList, TrendingUp, CalendarCheck, ArrowRight, Loader2, KeyRound, CalendarClock, MailCheck } from "lucide-react";
import { formatApiError, useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, refresh } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [error, setError] = useState("");
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwState, setPwState] = useState({ saving: false, error: "", ok: false });
  const [dueTasks, setDueTasks] = useState(null);
  const [emailHealth, setEmailHealth] = useState(null);
  const [testSend, setTestSend] = useState({ sending: false, msg: "" });

  const sendTestEmail = async () => {
    setTestSend({ sending: true, msg: "" });
    try {
      const r = await axios.post(`${API}/admin/system/email-test`);
      setTestSend({ sending: false, msg: `Test email sent to ${r.data.to} — check the inbox (and spam folder).` });
    } catch (err) {
      setTestSend({ sending: false, msg: formatApiError(err) });
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      setPwState({ saving: false, error: "New passwords do not match.", ok: false });
      return;
    }
    setPwState({ saving: true, error: "", ok: false });
    try {
      await axios.post(`${API}/auth/change-password`, { currentPassword: pw.current, newPassword: pw.next });
      setPwState({ saving: false, error: "", ok: true });
      setPw({ current: "", next: "", confirm: "" });
      await refresh();
    } catch (err) {
      setPwState({ saving: false, error: formatApiError(err), ok: false });
    }
  };

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [s, r] = await Promise.all([
          axios.get(`${API}/admin/leads/summary`),
          axios.get(`${API}/admin/leads?limit=8`),
        ]);
        setSummary(s.data);
        setRecent(r.data.leads);
      } catch (err) {
        setError(formatApiError(err));
      }
      try {
        const t = await axios.get(`${API}/admin/tasks`);
        setDueTasks(t.data);
      } catch (_) {}
      if (["admin", "manager"].includes(user.role)) {
        try {
          const h = await axios.get(`${API}/admin/system/email-status`);
          setEmailHealth(h.data);
        } catch (_) {}
      }
    })();
  }, [user]);

  if (error) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="flex items-center justify-center h-40 text-mid-neutral">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {user?.mustChangePassword && (
        <section data-testid="change-password-banner" className="rounded-2xl border border-yellow-300 bg-yellow-50 p-5">
          <h2 className="flex items-center gap-2 text-base font-extrabold text-forest">
            <KeyRound className="h-4 w-4" /> Set your new password
          </h2>
          <p className="mt-1 text-sm text-mid-neutral">
            Your account was created with a temporary password. Choose a new one (8+ characters) to secure it.
          </p>
          <form onSubmit={changePassword} className="mt-4 grid sm:grid-cols-4 gap-3">
            <input
              type="password" required placeholder="Current (temporary) password"
              value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })}
              data-testid="change-password-current"
              className="p-2.5 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest"
            />
            <input
              type="password" required minLength={8} placeholder="New password"
              value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })}
              data-testid="change-password-new"
              className="p-2.5 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest"
            />
            <input
              type="password" required minLength={8} placeholder="Confirm new password"
              value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
              data-testid="change-password-confirm"
              className="p-2.5 rounded-xl bg-white border border-border-neutral focus:border-mint focus:outline-none text-sm text-forest"
            />
            <button
              type="submit" disabled={pwState.saving}
              data-testid="change-password-submit"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold disabled:opacity-50"
            >
              {pwState.saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Update
            </button>
          </form>
          {pwState.error && (
            <p data-testid="change-password-error" className="mt-2 text-sm text-red-700">{pwState.error}</p>
          )}
          {pwState.ok && (
            <p data-testid="change-password-success" className="mt-2 text-sm text-green-700">Password updated.</p>
          )}
        </section>
      )}
      <header>
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">Dashboard</p>
        <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">
          Welcome back
        </h1>
        <p className="mt-2 text-mid-neutral">
          Overview of leads across Growth Assessments and Discovery Bookings.
        </p>
      </header>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total leads"
          value={summary.total}
          Icon={TrendingUp}
          testId="stat-total"
        />
        <StatCard
          label="Growth Assessments"
          value={summary.bySource["growth-assessment"] || 0}
          Icon={ClipboardList}
          testId="stat-growth"
        />
        <StatCard
          label="Discovery Bookings"
          value={summary.bySource["discovery-booking"] || 0}
          Icon={CalendarCheck}
          testId="stat-discovery"
        />
        <StatCard
          label="Won"
          value={summary.byStage["Won"] || 0}
          Icon={TrendingUp}
          testId="stat-won"
          accent
        />
      </div>

      {dueTasks && (
        <section data-testid="dashboard-tasks" className="rounded-2xl bg-white border border-border-neutral p-5">
          <div className="flex items-end justify-between">
            <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
              <CalendarClock className="h-4 w-4 text-mint" /> Tasks due
              {dueTasks.dueCount > 0 && (
                <span data-testid="tasks-due-count" className="rounded-full bg-yellow-100 text-yellow-800 px-2.5 py-0.5 text-xs font-bold">
                  {dueTasks.dueCount}
                </span>
              )}
            </h2>
            <Link to="/admin/tasks" data-testid="dashboard-view-tasks" className="text-sm font-semibold text-forest inline-flex items-center gap-1">
              All tasks <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-3 space-y-2">
            {dueTasks.tasks
              .filter((t) => t.status === "Open" && (t.isOverdue || t.dueToday))
              .slice(0, 5)
              .map((t) => (
                <Link
                  key={t.id}
                  to={t.relatedType === "lead" ? `/admin/leads/${t.relatedId}` : `/admin/clients/${t.relatedId}`}
                  data-testid={`dashboard-task-${t.id}`}
                  className="flex items-center gap-3 rounded-xl bg-soft/50 p-3 hover:bg-soft transition-colors"
                >
                  <span className="flex-1 text-sm font-semibold text-forest truncate">{t.title}</span>
                  <span className="text-xs text-mid-neutral">{t.relatedName}</span>
                  <span className={"rounded-full px-2.5 py-0.5 text-[11px] font-bold " + (t.isOverdue ? "bg-red-100 text-red-800" : "bg-yellow-100 text-yellow-800")}>
                    {t.isOverdue ? "Overdue" : "Due today"}
                  </span>
                </Link>
              ))}
            {dueTasks.dueCount === 0 && (
              <p className="text-sm text-mid-neutral">Nothing due today. Nice.</p>
            )}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-lg font-extrabold text-forest">Pipeline</h2>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(summary.byStage).map(([stage, count]) => (
            <div
              key={stage}
              data-testid={`stage-${stage.toLowerCase().replace(/\s+/g,'-')}`}
              className="rounded-xl bg-white border border-border-neutral p-4"
            >
              <p className="text-xs font-semibold text-mid-neutral leading-tight">{stage}</p>
              <p className="mt-2 text-2xl font-extrabold text-forest">{count}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-end justify-between">
          <h2 className="text-lg font-extrabold text-forest">Recent leads</h2>
          <Link
            to="/admin/leads"
            data-testid="dashboard-view-all-leads"
            className="text-sm font-semibold text-forest inline-flex items-center gap-1"
          >
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-4 rounded-2xl bg-white border border-border-neutral overflow-hidden">
          {recent.length === 0 && (
            <div className="p-6 text-sm text-mid-neutral text-center">
              No leads yet.
            </div>
          )}
          {recent.map((l) => (
            <Link
              key={l.id}
              to={`/admin/leads/${l.id}`}
              className="flex items-center gap-4 p-4 border-b border-border-neutral last:border-0 hover:bg-soft/40 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-bold text-forest truncate">
                  {l.firstName} {l.lastName} <span className="text-mid-neutral font-normal">· {l.email}</span>
                </p>
                <p className="text-xs text-mid-neutral truncate">
                  {l.businessName || "—"} · {l.sourceLabel}
                </p>
              </div>
              <StagePill stage={l.stage} />
            </Link>
          ))}
        </div>
      </section>

      {emailHealth && (
        <section data-testid="email-health" className="rounded-2xl bg-white border border-border-neutral p-5">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
                <MailCheck className="h-4 w-4 text-mint" /> Email deliverability
              </h2>
              <p className="mt-1 text-xs text-mid-neutral">
                Sending as {emailHealth.fromEmail} via {emailHealth.smtpHost} · alerts to {emailHealth.notificationEmail}
              </p>
            </div>
            <button
              type="button"
              onClick={sendTestEmail}
              disabled={testSend.sending}
              data-testid="email-test-send"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
            >
              {testSend.sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Send test email
            </button>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <DnsBadge label="SPF" ok={emailHealth.spf.ok} />
            <DnsBadge label="DKIM" ok={emailHealth.dkim.ok} />
            <DnsBadge label="DMARC" ok={emailHealth.dmarc.ok} />
            <DnsBadge label="MX" ok={emailHealth.mx.ok} />
            {emailHealth.bimi && <DnsBadge label="BIMI" ok={emailHealth.bimi.ok} />}
          </div>
          {emailHealth.bimi && !emailHealth.bimi.ok && (
            <p data-testid="bimi-missing" className="mt-3 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              BIMI (sender logo/avatar in inboxes) is not set up yet. Add a TXT record at
              default._bimi.{emailHealth.domain} with the value provided by the Green Circle team,
              then come back here to re-check.
            </p>
          )}
          {!emailHealth.dkim.ok && (
            <p data-testid="dkim-missing" className="mt-3 text-xs text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              DKIM is not set up for {emailHealth.domain} yet. Without it, booking confirmations may keep landing in spam.
              Add the DKIM record from your email hosting panel (cPanel → Email Deliverability → {emailHealth.domain}),
              then come back here to re-check.
            </p>
          )}
          {testSend.msg && (
            <p data-testid="email-test-msg" className="mt-3 text-xs font-semibold text-forest">{testSend.msg}</p>
          )}
        </section>
      )}
    </div>
  );
}

function StatCard({ label, value, Icon, accent = false, testId }) {
  return (
    <div
      data-testid={testId}
      className={
        "rounded-2xl p-5 " +
        (accent
          ? "bg-forest text-white"
          : "bg-white border border-border-neutral text-forest")
      }
    >
      <div className="flex items-center justify-between">
        <p className={"text-xs font-bold uppercase tracking-[0.15em] " + (accent ? "text-lime" : "text-mid-neutral")}>
          {label}
        </p>
        <Icon className={"h-4 w-4 " + (accent ? "text-lime" : "text-mint")} />
      </div>
      <p className="mt-3 text-3xl font-extrabold">{value}</p>
    </div>
  );
}

export function StagePill({ stage }) {
  const colours = {
    "New": "bg-soft text-forest",
    "Contacted": "bg-yellow-100 text-yellow-800",
    "Discovery Call Booked": "bg-mint/20 text-forest",
    "Qualified": "bg-blue-100 text-blue-800",
    "Proposal": "bg-purple-100 text-purple-800",
    "Won": "bg-lime text-forest",
    "Lost": "bg-red-100 text-red-800",
  };
  return (
    <span className={"inline-block rounded-full px-3 py-1 text-xs font-bold shrink-0 " + (colours[stage] || "bg-soft text-forest")}>
      {stage}
    </span>
  );
}

function DnsBadge({ label, ok }) {
  return (
    <span
      data-testid={`dns-${label.toLowerCase()}`}
      className={"rounded-full px-3 py-1 text-xs font-bold " + (ok ? "bg-lime text-forest" : "bg-red-100 text-red-800")}
    >
      {label} {ok ? "OK" : "missing"}
    </span>
  );
}
